import type { ActionType } from '@features/instructors/hooks/use-instructor-discipline-actions/useInstructorDisciplineActions.types';

import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { FLAG_MESSAGE_DURATION } from '@shared/constants/flagMessage.constants';
import * as React from 'react';

import type {
  InstructorActionFeedbackSnapshot,
  UseInstructorActionFeedbackParams,
  UseInstructorActionFeedbackReturn,
} from './useInstructorActionFeedback.types';

const SUCCESS_MESSAGES: Record<ActionType, (name: string) => string> = {
  Approve: name => `Approval request received for ${name}.`,
  Revoke: name => `Revocation request received for ${name}.`,
  CancelApproval: name => `Cancel request successful for ${name}.`,
  CancelRevocation: name => `Cancel request successful for ${name}.`,
};

const ERROR_MESSAGES: Record<ActionType, (name: string) => string> = {
  Approve: name => `Approval request failed for ${name}.`,
  Revoke: name => `Revocation request failed for ${name}.`,
  CancelApproval: name =>
    `Cancel request failed for ${name}. Please try again.`,
  CancelRevocation: name =>
    `Cancel request failed for ${name}. Please try again.`,
};

export function useInstructorActionFeedback({
  lastActionResult,
  clearLastActionResult,
  instructorDisplayName,
}: UseInstructorActionFeedbackParams): UseInstructorActionFeedbackReturn {
  const [feedbackSnapshot, setFeedbackSnapshot] =
    React.useState<InstructorActionFeedbackSnapshot | null>(null);
  const [feedbackModalOpen, setFeedbackModalOpen] =
    React.useState<boolean>(false);

  const closeFeedbackModal = React.useCallback(
    () => setFeedbackModalOpen(false),
    []
  );

  React.useEffect(() => {
    if (!lastActionResult) return;

    const { status, discipline, programId, action } = lastActionResult;

    setFeedbackSnapshot({
      status,
      disciplineName: discipline.name,
      programId,
      action,
    });
    clearLastActionResult();

    const displayName = instructorDisplayName ?? '';
    const message =
      status === 'success'
        ? SUCCESS_MESSAGES[action](displayName)
        : ERROR_MESSAGES[action](displayName);
    const flagType = status === 'success' ? 'success' : 'danger';
    const hasDetailedFeedback = action === 'Approve' || action === 'Revoke';

    emitFlagMessage(
      flagType,
      message,
      FLAG_MESSAGE_DURATION,
      hasDetailedFeedback,
      'View details',
      () => setFeedbackModalOpen(true)
    );
  }, [lastActionResult, clearLastActionResult, instructorDisplayName]);

  return {
    feedbackSnapshot,
    feedbackModalOpen,
    closeFeedbackModal,
  };
}
